import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { formatApiErrorDetail } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay un token guardado
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Obtener información del usuario
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/v1/auth/me');
      const userData = response.data;
      setUser(userData);
      setIsAuthenticated(true);
      // Guardar el rol en localStorage para uso inmediato
      if (userData && userData.rol) {
        localStorage.setItem('userRole', userData.rol.toString());
      }
      return userData;
    } catch (error) {
      console.error('Error al obtener usuario:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post('/api/v1/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setIsAuthenticated(true);
      const userData = await fetchUser();
      
      // Guardar el rol temporalmente para uso inmediato en Login
      if (userData && userData.rol) {
        localStorage.setItem('userRole', userData.rol.toString());
      }
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      return {
        success: false,
        message: formatApiErrorDetail(error, 'Error al iniciar sesión'),
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

